// coordinate-input.component.ts
import { NgSwitch, NgSwitchCase } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  computed,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormBuilder, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { debounceTime } from 'rxjs/operators';
// Button import removed
import { FloatLabel } from 'primeng/floatlabel';
import { InputNumber } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';

export type FormatType = 'decimal' | 'dm' | 'dms';

@Component({
  selector: 'lib-coordinate-input',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoordinateInputComponent),
      multi: true,
    },
  ],
  imports: [
    ReactiveFormsModule,
    InputNumber,
    SelectButtonModule,
    NgSwitch,
    NgSwitchCase,
    FormsModule,
    FloatLabel,
    TranslocoPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div>
      <div class="mb-3">
        <p-selectbutton
          [options]="formatOptions()"
          [(ngModel)]="formatSignal"
          (ngModelChange)="setFormat($event)"
          optionLabel="label"
          optionValue="value"
          size="small"
          [allowEmpty]="false">
        </p-selectbutton>
      </div>

      <form [formGroup]="fg">
        <ng-container [ngSwitch]="formatSignal()">
          <!-- DECIMAL -->
          <div *ngSwitchCase="'decimal'" class="grid grid-cols-2 gap-2">
            <div>
              <label class="mb-1 block text-xs font-medium text-gray-500">{{
                'shared.coordinateInput.latitude' | transloco
              }}</label>
              <p-input-number
                formControlName="latDec"
                [min]="-90"
                [max]="90"
                mode="decimal"
                maxFractionDigits="12"
                styleClass="w-full"
                [placeholder]="'shared.coordinateInput.range' | transloco: { min: -90, max: 90 }"></p-input-number>
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-gray-500">{{
                'shared.coordinateInput.longitude' | transloco
              }}</label>
              <p-input-number
                formControlName="lngDec"
                [min]="-180"
                [max]="180"
                mode="decimal"
                maxFractionDigits="12"
                [placeholder]="'shared.coordinateInput.range' | transloco: { min: -180, max: 180 }"
                styleClass="w-full"></p-input-number>
            </div>
          </div>

          <!-- DM -->
          <div *ngSwitchCase="'dm'" class="flex flex-col gap-2.5">
            <div>
              <label class="mb-1 block text-xs font-medium text-gray-500">{{
                'shared.coordinateInput.latitude' | transloco
              }}</label>
              <div class="flex items-center gap-2">
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="latDeg"
                    [min]="0"
                    [max]="90"
                    mode="decimal"
                    styleClass="w-full"
                    [useGrouping]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.degrees' | transloco }}</label>
                </p-floatlabel>
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="latMin"
                    [min]="0"
                    [max]="59.999"
                    mode="decimal"
                    maxFractionDigits="12"
                    styleClass="w-full"
                    [useGrouping]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.minutes' | transloco }}</label>
                </p-floatlabel>
                <p-selectbutton
                  [options]="latDirections()"
                  formControlName="latDir"
                  optionLabel="label"
                  optionValue="value"
                  allowEmpty="false"
                  size="small"></p-selectbutton>
              </div>
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-gray-500">{{
                'shared.coordinateInput.longitude' | transloco
              }}</label>
              <div class="flex items-center gap-2">
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="lngDeg"
                    [min]="0"
                    [max]="180"
                    styleClass="w-full"
                    [useGrouping]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.degrees' | transloco }}</label>
                </p-floatlabel>
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="lngMin"
                    [min]="0"
                    [max]="59.99999"
                    maxFractionDigits="12"
                    mode="decimal"
                    styleClass="w-full"
                    [useGrouping]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.minutes' | transloco }}</label>
                </p-floatlabel>
                <p-selectbutton
                  [options]="lngDirections()"
                  formControlName="lngDir"
                  optionLabel="label"
                  optionValue="value"
                  allowEmpty="false"
                  size="small"></p-selectbutton>
              </div>
            </div>
          </div>

          <!-- DMS -->
          <div *ngSwitchCase="'dms'" class="flex flex-col gap-2.5">
            <div>
              <label class="mb-1 block text-xs font-medium text-gray-500">{{
                'shared.coordinateInput.latitude' | transloco
              }}</label>
              <div class="flex items-center gap-2">
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="latDeg"
                    [min]="0"
                    [max]="90"
                    styleClass="w-full"
                    [showButtons]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.degrees' | transloco }}</label>
                </p-floatlabel>
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="latMin"
                    [min]="0"
                    [max]="59"
                    styleClass="w-full"
                    [showButtons]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.minutes' | transloco }}</label>
                </p-floatlabel>
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="latSec"
                    mode="decimal"
                    [min]="0"
                    [max]="59.999999999999"
                    maxFractionDigits="12"
                    styleClass="w-full"
                    [showButtons]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.seconds' | transloco }}</label>
                </p-floatlabel>
                <p-selectbutton
                  allowEmpty="false"
                  [options]="latDirections()"
                  formControlName="latDir"
                  optionLabel="label"
                  optionValue="value"
                  size="small"></p-selectbutton>
              </div>
            </div>
            <div>
              <label class="mb-1 block text-xs font-medium text-gray-500">{{
                'shared.coordinateInput.longitude' | transloco
              }}</label>
              <div class="flex items-center gap-2">
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="lngDeg"
                    [min]="0"
                    [max]="180"
                    maxFractionDigits="0"
                    styleClass="w-full"
                    [showButtons]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.degrees' | transloco }}</label>
                </p-floatlabel>
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="lngMin"
                    [min]="0"
                    [max]="59"
                    styleClass="w-full"
                    [showButtons]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.minutes' | transloco }}</label>
                </p-floatlabel>
                <p-floatlabel variant="on" class="flex-1">
                  <p-input-number
                    formControlName="lngSec"
                    mode="decimal"
                    [min]="0"
                    [max]="59.999999999999"
                    maxFractionDigits="12"
                    styleClass="w-full"
                    [showButtons]="false"></p-input-number>
                  <label>{{ 'shared.coordinateInput.seconds' | transloco }}</label>
                </p-floatlabel>
                <p-selectbutton
                  allowEmpty="false"
                  [options]="lngDirections()"
                  formControlName="lngDir"
                  optionLabel="label"
                  optionValue="value"
                  size="small"></p-selectbutton>
              </div>
            </div>
          </div>
        </ng-container>
      </form>
    </div>
  `,
})
export class CoordinateInputComponent implements ControlValueAccessor, OnChanges, OnInit {
  lat = input.required<number>();
  lng = input.required<number>();

  // Outputs
  latChange = output<number>();
  lngChange = output<number>();
  formatChange = output<FormatType>();

  // === CALLBACKS FOURNIS PAR ANGULAR ===
  formatSignal = signal<FormatType>('dms');
  prevFormat: FormatType = 'decimal';
  /**
   * Option labels (format + hemispheres) follow the active language: transloco keys
   * `shared.coordinateInput.*`, root dictionaries of each app. Values stay N/S/E/W.
   */
  readonly #labels = toSignal(
    inject(TranslocoService).selectTranslate<string[]>([
      'shared.coordinateInput.format.decimal',
      'shared.coordinateInput.format.dm',
      'shared.coordinateInput.format.dms',
      'shared.coordinateInput.direction.north',
      'shared.coordinateInput.direction.south',
      'shared.coordinateInput.direction.east',
      'shared.coordinateInput.direction.west',
    ]),
    { initialValue: [] as string[] }
  );
  readonly formatOptions = computed(() => {
    const [decimal, dm, dms] = this.#labels();
    return [
      { label: decimal, value: 'decimal' },
      { label: dm, value: 'dm' },
      { label: dms, value: 'dms' },
    ];
  });
  readonly latDirections = computed(() => {
    const [, , , north, south] = this.#labels();
    return [
      { label: north, value: 'N' },
      { label: south, value: 'S' },
    ];
  });
  readonly lngDirections = computed(() => {
    const [, , , , , east, west] = this.#labels();
    return [
      { label: east, value: 'E' },
      { label: west, value: 'W' },
    ];
  });
  fg = inject(FormBuilder).nonNullable.group({
    // decimal
    latDec: [0],
    lngDec: [0],
    // dm
    latDeg: [0],
    latMin: [0],
    latDir: ['N'],
    // dms
    latSec: [0],
    // idem pour lng
    lngDeg: [0],
    lngMin: [0],
    lngDir: ['E'],
    lngSec: [0],
  });
  private onTouched!: () => void;

  get value(): { lat: number; lng: number } | undefined {
    // Ensure coordinates are calculated before returning the value
    if (this.formatSignal() !== 'decimal') {
      // Calculate coordinates from DM or DMS before returning
      this.calculateCoordinates();
    }

    // renvoie toujours le format décimal, peu importe
    // le format d'affichage
    const v = this.fg.value;
    if (v.latDec == null || v.lngDec == null) return undefined;

    return { lat: v.latDec, lng: v.lngDec };
  }

  ngOnChanges() {
    this.fg.patchValue({ latDec: this.lat(), lngDec: this.lng() }, { emitEvent: false });
    this.applyFormat(this.formatSignal());
  }

  forceCalculateAndGetValue(): { lat: number; lng: number } {
    return this.calculateCoordinates();
  }

  calculateCoordinates() {
    const v = this.fg.value;
    let lat: number, lng: number;

    if (this.formatSignal() === 'dm') {
      lat = (v.latDeg! + v.latMin! / 60) * (v.latDir === 'S' ? -1 : 1);
      lng = (v.lngDeg! + v.lngMin! / 60) * (v.lngDir === 'W' ? -1 : 1);
    } else if (this.formatSignal() === 'dms') {
      lat = (v.latDeg! + v.latMin! / 60 + v.latSec! / 3600) * (v.latDir === 'S' ? -1 : 1);
      lng = (v.lngDeg! + v.lngMin! / 60 + v.lngSec! / 3600) * (v.lngDir === 'W' ? -1 : 1);
    } else {
      lat = v.latDec!;
      lng = v.lngDec!;
    }

    // Update decimal values without triggering another form value change
    if (this.formatSignal() !== 'decimal') {
      this.fg.patchValue({ latDec: lat, lngDec: lng }, { emitEvent: false });
    }

    this.onChange({ lat, lng });
    this.onTouched?.();

    return { lat, lng };
  }

  ngOnInit(): void {
    this.fg.valueChanges
      .pipe(
        debounceTime(300) // Wait 300ms after last input before calculating
      )
      .subscribe(() => {
        if (this.formatSignal() === 'decimal') {
          this.calculateCoordinates();
        }
      });
  }

  writeValue(value: { lat: number; lng: number } | null): void {
    if (value) {
      // on met à jour notre form interne sans déclencher valueChanges
      this.fg.patchValue(
        {
          latDec: value.lat,
          lngDec: value.lng,
        },
        { emitEvent: false }
      );
      // on peut ensuite re-découper en DM/DMS si nécessaire
      this.applyFormat(this.formatSignal());
    } else {
      // Reset all form values when null is passed (when dialog is closed)
      this.fg.patchValue(
        {
          latDec: undefined,
          lngDec: undefined,
          latDeg: undefined,
          latMin: undefined,
          latSec: undefined,
          lngDeg: undefined,
          lngMin: undefined,
          lngSec: undefined,
          latDir: 'N',
          lngDir: 'E',
        },
        { emitEvent: false }
      );
    }
  }

  registerOnChange(fn: (value: { lat: number; lng: number }) => void): void {
    // Store the callback from Angular
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.fg.disable({ emitEvent: false }) : this.fg.enable({ emitEvent: false });
  }

  setFormat(newFormat: FormatType) {
    const prev = this.prevFormat;
    this.prevFormat = newFormat;
    this.formatSignal.set(newFormat);
    if (prev != 'decimal') this.computeFrom(prev);
    this.applyFormat(newFormat);

    this.formatChange.emit(newFormat);
    this.onTouched?.();
  }

  // This will hold the actual callback function provided by Angular
  private onChangeCallback: (value: { lat: number; lng: number }) => void = () => {
    // This empty method will be replaced by Angular's callback
  };

  // Our wrapper to ensure coordinates are calculated when needed
  private onChange(value: { lat: number; lng: number }): void {
    this.onChangeCallback(value);
  }

  private applyFormat(fmt: FormatType) {
    if (fmt != 'decimal') this.fillFromDecimal(fmt);
  }

  private computeFrom(format: 'dm' | 'dms'): void {
    const v = this.fg.value;

    // Guard : on doit toujours avoir latDeg, latMin, lngDeg, lngMin
    // et pour 'dms' il faut aussi latSec, lngSec
    if (
      v.latDeg == null ||
      v.latMin == null ||
      v.lngDeg == null ||
      v.lngMin == null ||
      (format === 'dms' && (v.latSec == null || v.lngSec == null))
    ) {
      return;
    }

    // 1) calcule la valeur absolue
    let lat = v.latDeg + v.latMin / 60;
    let lng = v.lngDeg + v.lngMin / 60;

    if (format === 'dms') {
      lat += v.latSec! / 3600;
      lng += v.lngSec! / 3600;
    }

    // 2) applique le signe
    lat *= v.latDir === 'S' ? -1 : 1;
    lng *= v.lngDir === 'W' ? -1 : 1;

    // 3) patch + émission
    this.fg.patchValue({ latDec: lat, lngDec: lng }, { emitEvent: false });
    this.latChange.emit(lat);
    this.lngChange.emit(lng);
    this.onChange({ lat, lng });
    this.onTouched?.();
  }

  /**
   * Découpe latDec/lngDec en DM ou DMS selon le format passé en paramètre.
   */
  private fillFromDecimal(format: 'dm' | 'dms'): void {
    const v = this.fg.value;
    const lat = v.latDec,
      lng = v.lngDec;
    if (lat == null || lng == null) return;

    const absLat = Math.abs(lat),
      absLng = Math.abs(lng);
    const signLat = lat >= 0 ? 'N' : 'S';
    const signLng = lng >= 0 ? 'E' : 'W';
    const degLat = Math.floor(absLat),
      degLng = Math.floor(absLng);
    const remLat = (absLat - degLat) * 60;
    const remLng = (absLng - degLng) * 60;

    if (format === 'dm') {
      // DM : minutes décimales
      this.fg.patchValue(
        {
          latDeg: degLat,
          latMin: remLat,
          latDir: signLat,
          lngDeg: degLng,
          lngMin: remLng,
          lngDir: signLng,
        },
        { emitEvent: false }
      );
    } else {
      // DMS : minutes entières + secondes
      // Using toFixed and parseFloat to maintain precision for seconds
      const minLat = Math.floor(remLat);
      const minLng = Math.floor(remLng);

      // Calculate seconds with high precision
      const secLat = parseFloat(((remLat - minLat) * 60).toFixed(12));
      const secLng = parseFloat(((remLng - minLng) * 60).toFixed(12));

      this.fg.patchValue(
        {
          latDeg: degLat,
          latMin: minLat,
          latSec: secLat,
          latDir: signLat,
          lngDeg: degLng,
          lngMin: minLng,
          lngSec: secLng,
          lngDir: signLng,
        },
        { emitEvent: false }
      );
    }
  }
}
